/**
 * Человекочитаемые тексты событий student_events. Единый словарь для
 * вкладки «История» в карточке ученика (StudentDetailModal) и отчёта
 * «Действия пользователей». Типы и payload — соглашение с бэком
 * (App\Models\StudentEvent).
 */

// Русские подписи полей для событий вида «изменён … : поля».
const EVENT_FIELD_LABELS = {
  // ученик
  family_name: 'фамилия', first_name: 'имя', mid_name: 'отчество', sex: 'пол',
  birthdate: 'дата рождения', phone: 'телефон', email: 'email', status: 'статус',
  archive_destination: 'куда переведён',
  // договор
  number: 'номер', date: 'дата договора', date_start: 'срок действия с',
  date_end: 'срок действия до', signatory_id: 'заказчик', year_study_id: 'учебный год',
  tariff_id: 'тариф', admission_fee: 'вступительный взнос',
  admission_fee_due_date: 'срок оплаты взноса', class: 'класс',
};

const EVENT_AGR_TYPE = {
  school: 'школа', kindergarten: 'детский сад',
  paid_services: 'доп. услуги', extras: 'доп. услуги',
};

// Короткие русские названия типов — для селекта фильтра в отчёте.
export const EVENT_TYPE_LABELS = {
  student_created: 'Карточка создана',
  student_updated: 'Карточка изменена',
  student_archived: 'Ученик в архив',
  student_restored: 'Ученик из архива',
  agreement_created: 'Договор создан',
  agreement_updated: 'Договор изменён',
  agreement_deleted: 'Договор удалён',
  agreement_signed: 'Договор подписан',
  agreement_unsigned: 'Снята отметка подписания',
  agreement_terminated: 'Договор расторгнут',
  agreement_termination_removed: 'Отмена расторжения',
  agreement_scan_uploaded: 'Загружен скан договора',
  agreement_scan_removed: 'Удалён скан договора',
  class_enrolled: 'Зачисление в класс',
  class_enrollment_updated: 'Изменён период в классе',
  class_enrollment_removed: 'Удалено зачисление',
  contact_added: 'Контакт добавлен',
  contact_updated: 'Контакт изменён',
  contact_removed: 'Контакт удалён',
  lk_access_linked: 'Доступ родителя в ЛК дан',
  lk_access_unlinked: 'Доступ родителя в ЛК отозван',
  payment_reassigned: 'Платёж перенесён',
};

export function fmtEventDate(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso);
}

export function fmtEventDateTime(dt) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(dt || ''));
  return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}` : String(dt || '');
}

// Человекочитаемый текст события из type + payload.
export function eventText(e) {
  const p = e.payload || {};
  const num = p.number ? `№ ${p.number}` : '';
  const typeRu = EVENT_AGR_TYPE[p.type];
  const agr = `договор ${typeRu ? `(${typeRu}) ` : ''}${num}`.trim();
  const changed = (p.changed || []).map((k) => EVENT_FIELD_LABELS[k] || k).join(', ');
  const period = p.dateStart ? ` (${fmtEventDate(p.dateStart)} — ${fmtEventDate(p.dateEnd)})` : '';
  switch (e.type) {
    case 'student_created':   return 'Карточка ученика создана';
    case 'student_updated':   return 'Изменены данные ученика' + (changed ? `: ${changed}` : '');
    case 'student_archived':
      return 'Ученик переведён в архив' + (p.destination ? ` → ${p.destination}` : '');
    case 'student_restored':  return 'Ученик возвращён из архива';
    case 'agreement_created': return `Создан ${agr}`;
    case 'agreement_updated': return `Изменён ${agr}` + (changed ? `: ${changed}` : '');
    case 'agreement_deleted': return `Удалён ${agr}`;
    case 'agreement_signed':   return `Договор ${num} отмечен подписанным`;
    case 'agreement_unsigned': return `С договора ${num} снята отметка о подписании`;
    case 'agreement_terminated':
      return `Договор ${num} расторгнут${p.date ? ' с ' + fmtEventDate(p.date) : ''}`
        + (p.reason ? ` — ${p.reason}` : '');
    case 'agreement_termination_removed': return `Отменено расторжение договора ${num}`;
    case 'agreement_scan_uploaded': return `Загружен скан договора ${num}`;
    case 'agreement_scan_removed':  return `Удалён скан договора ${num}`;
    case 'class_enrolled':            return `Зачисление в класс ${p.classLabel || '?'}${period}`;
    case 'class_enrollment_updated':  return `Изменён период обучения в классе ${p.classLabel || '?'}${period}`;
    case 'class_enrollment_removed':  return `Удалено зачисление в класс ${p.classLabel || '?'}${period}`;
    case 'contact_added':   return `Добавлено контактное лицо: ${p.name || '?'}`;
    case 'contact_updated': return `Изменено контактное лицо: ${p.name || '?'}`;
    case 'contact_removed': return `Удалено контактное лицо: ${p.name || '?'}`;
    case 'lk_access_linked':
      return `Учётке родителя «${p.parentName || '?'}» дан доступ к ученику в ЛК`;
    case 'lk_access_unlinked':
      return `У учётки родителя «${p.parentName || '?'}» отозван доступ к ученику в ЛК`;
    case 'payment_reassigned':
      return `Платёж #${p.paymentId}${p.amount ? ` (${Number(p.amount).toLocaleString('ru-RU')} ₽)` : ''}`
        + ` перенесён с договора ${p.fromNumber || '—'} на ${p.toNumber || '?'}`;
    default: return e.type;
  }
}

// Иконка по типу события — чисто визуальная навигация по ленте.
export function eventIcon(type) {
  if (type.startsWith('agreement_')) return 'doc';
  if (type.startsWith('class_'))     return 'grid';
  if (type.startsWith('contact_'))   return 'users';
  return 'edit';
}
